import { Controller } from '@hotwired/stimulus';
import { find, maxBy, remove } from 'lodash';
import LayoutSection from '../../models/layout_section';
import EventPublisher from '../../utilities/event_publisher';

export default class extends Controller {
  static targets = [
    'sectionList',
    'settingsContainer',
    'sortOrder',
    'identifier',
    'listItem',
    'actionBtn',
    'actionTitle',
    'addNewSection',
    'identifierInfo',
    'identifierInfoContainer',
    'highlightPositions',
    'showPositionsContainer'
  ];

  initialize() {
    this.MAX_NUM_OF_SECTIONS = 6;
  }

  connect() {
    window.addEventListener('sections-loaded', this.onSectionsLoaded);
    window.addEventListener('section-member-changed', this.onSectionMembershipsChanged);
  }

  onSectionsLoaded = event => {
    this.sections = event.detail.sections;
    this.renderSectionsList();
  }

  onSectionMembershipsChanged = event => {
    const { previousSectionId, currentSectionId, positionId } = event.detail;
    const previousSection = find(this.sections, { id: parseInt(previousSectionId || 0) });
    const currentSection = find(this.sections, { id: parseInt(currentSectionId) });
    previousSection?.deleteMembership(positionId);
    if (previousSection?.isHighlighted) EventPublisher.publish('position-unhighlighted', { positionId: positionId });
    currentSection?.addMembership(positionId);
    if (currentSection?.isHighlighted) EventPublisher.publish('position-highlighted', { positionId: positionId });
  }

  handleSectionSelected = event => {
    const { id } = event.currentTarget.dataset;
    this.section = find(this.sections, { id: parseInt(id) });
    this.showSectionEditForm();
    this.populateSortOrderOptions(this.sections.length);
  }

  handleSectionRemoved = () => {
    const confirmed = confirm(`Are you sure you want to remove section ${this.section.identifier}?`); // eslint-disable-line no-alert
    if (!confirmed) return;
    this.section.unhighlightPositions();
    remove(this.sections, { id: this.section.id }); //eslint-disable-line lodash/prefer-immutable-method
    this.renderSectionsList();
    this.refreshAddSection();
    this.settingsContainerTarget.classList.add('hidden');
    EventPublisher.publish('section-removed', { sectionId: this.section.id });
  }

  handleSectionIdentifierChanged = event => {
    const listItem = this.getListItem();
    this.displaySectionIdentifierInfo(event.target.value);
    this.section.identifier = event.target.value;
    listItem.outerHTML = this.renderSection(this.section);
  }

  handleNewSectionIdentifierChanged = event => {
    this.displaySectionIdentifierInfo(event.target.value);
  }

  handleSortOrderChanged = event => {
    this.section.sortOrder = parseInt(event.target.value);
    this.renderSectionsList();
  }

  handleCreateNewSection = () => {
    this.showSectionNewForm();
    this.populateSortOrderOptions(this.sections.length + 1);
  }

  handleAddSection = () => {
    if (this.identifierTarget.value.length < 1) return;
    const maxID = maxBy(this.sections, 'id')?.id || 0;
    const section = new LayoutSection(
      maxID + 1,
      this.identifierTarget.value,
      parseInt(this.sortOrderTarget.value),
      []
    );
    this.sections.push(section);
    this.settingsContainerTarget.classList.add('hidden');
    this.refreshAddSection();
    this.renderSectionsList();
  }

  handleHighlightPositionsChanged = () => {
    this.section.isHighlighted = this.highlightPositionsTarget.checked;
    if (this.section.isHighlighted) this.section.highlightPositions();
    else this.section.unhighlightPositions();
  }

  showSectionNewForm() {
    this.settingsContainerTarget.classList.remove('hidden');
    this.actionTitleTarget.innerHTML = 'New Section';
    this.actionBtnTarget.innerHTML = 'Add Section';
    this.actionBtnTarget.classList.remove(...this.actionBtnTarget.classList);
    this.actionBtnTarget.classList.add('btn', 'btn-hollow');
    this.identifierTarget.value = '';
    this.actionBtnTarget.setAttribute('data-action', 'click->templated-layouts--section-settings#handleAddSection');
    this.identifierTarget.setAttribute('data-action', 'input->templated-layouts--section-settings#handleNewSectionIdentifierChanged');
    this.sortOrderTarget.removeAttribute('data-action');
    this.showPositionsContainerTarget.classList.add('hidden');
  }

  showSectionEditForm() {
    this.settingsContainerTarget.classList.remove('hidden');
    this.showPositionsContainerTarget.classList.remove('hidden')
    this.identifierTarget.value = this.section.identifier;
    this.highlightPositionsTarget.checked = this.section.isHighlighted;
    this.actionTitleTarget.innerHTML = 'Selected Section';
    this.actionBtnTarget.innerHTML = 'Remove Section';
    this.actionBtnTarget.classList.remove(...this.actionBtnTarget.classList);
    this.actionBtnTarget.classList.add('layout__remove-btn');
    this.actionBtnTarget.setAttribute('data-action', 'click->templated-layouts--section-settings#handleSectionRemoved');
    this.identifierTarget.setAttribute('data-action', 'input->templated-layouts--section-settings#handleSectionIdentifierChanged');
    this.sortOrderTarget.setAttribute('data-action', 'change->templated-layouts--section-settings#handleSortOrderChanged');
  }

  populateSortOrderOptions(optionsLength) {
    this.sortOrderTarget.innerHTML = null;
    for (let i = 1; i <= optionsLength; i++) {
      let selected = this.section?.sortOrder === i;
      this.sortOrderTarget.add(new Option(i, i, false, selected));
    }
  }

  displaySectionIdentifierInfo(identifier) {
    if (identifier.length < 1) {
      this.identifierInfoTarget.innerHTML = 'Section Identifier cannot be blank';
      this.identifierInfoContainerTarget.classList.add('text-red-600');
    } else {
      this.identifierInfoTarget.innerHTML = 'We suggest using very short identifiers, as space in the app is limited';
      this.identifierInfoContainerTarget.classList.remove('text-red-600');
    }
  }

  renderSectionsList() {
    let listHTML = '';
    this.sections.sort((a, b) => a.sortOrder - b.sortOrder);
    this.sections.forEach(section => {
      listHTML += this.renderSection(section);
    });
    this.sectionListTarget.innerHTML = listHTML;
  }

  renderSection(section) {
    return `
      <li class='mb-4 border-b cursor-pointer relative py-2'
          data-templated-layouts--section-settings-target='listItem'
          id='sectionItem-${section.id}'
          data-id='${section.id}'
          data-action='click->templated-layouts--section-settings#handleSectionSelected'>
        <span class='text-md'>${section.identifier}</span>
      </li>
    `;
  }

  refreshAddSection() {
    if (this.sections.length >= this.MAX_NUM_OF_SECTIONS) {
      this.addNewSectionTarget.classList.add('hidden');
    } else {
      this.addNewSectionTarget.classList.remove('hidden');
    }
  }

  getListItem() {
    return find(this.listItemTargets, { id: `sectionItem-${this.section.id}` });
  }
}
